import { useState, useEffect, createContext, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: "admin" | "organizer" | "staff" | "athlete" | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, organization?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<"admin" | "organizer" | "staff" | "athlete" | null>(null);
  const [loading, setLoading] = useState(true);

  const resolveRole = async (currentUser: User | null) => {
    if (!currentUser) {
      setRole(null);
      return;
    }

    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", currentUser.id)
      .limit(1)
      .maybeSingle();

    setRole((data?.role as "admin" | "organizer" | "staff" | "athlete" | undefined) ?? "athlete");
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      resolveRole(session?.user ?? null);
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      resolveRole(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const normalizeEmail = (value: string) => value.trim().toLowerCase();

  const signUp = async (email: string, password: string, fullName: string, organization = "") => {
    const { data: authData, error: signUpError } = await supabase.auth.signUp({
      email: normalizeEmail(email),
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          full_name: fullName,
          organization,
        },
      },
    });

    if (signUpError) {
      return { error: signUpError };
    }

    return { error: null };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizeEmail(email),
      password,
    });

    if (error?.message === "Invalid login credentials") {
      return {
        error: {
          message:
            "E-mail ou senha inválidos. Se você acabou de se cadastrar, confirme o e-mail e aguarde aprovação do administrador.",
        },
      };
    }

    if (error) return { error };

    // Check if user is approved (only blocks admin/organizer/staff roles, not athletes)
    const { data: profile } = await supabase
      .from("profiles")
      .select("status")
      .eq("user_id", data.user.id)
      .maybeSingle();

    if (profile && profile.status !== "approved") {
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", data.user.id)
        .maybeSingle();

      const requiresApproval = roleData?.role && ["admin", "organizer", "staff"].includes(roleData.role);

      if (requiresApproval) {
        await supabase.auth.signOut();
        return { error: { message: "Seu acesso ainda não foi liberado. Tente novamente mais tarde." } };
      }
    }

    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, role, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
